// Fachsprache und Aufrufe der Kennungspflege (ST-PTY-003, erster Schnitt).
//
// REINES MODELL, kein React, kein DOM -- dieselbe Trennung wie in
// contact-points.ts und aus demselben Grund.
//
// DAS WORT "IDENTIFIER" KOMMT AUF DEM BILDSCHIRM NICHT VOR. Auf dem Bildschirm
// heisst es "Kennung" (NFR-UX-003, CM-UX-010): das ist das Wort, mit dem eine
// Vertriebsmitarbeiterin nach der Verkehrsnummer einer Buchhandlung fragt.

import type { PlainMessage } from './access.ts';
import { countryName } from './addresses.ts';
import { readResource, sendCommand, type CommandMessages, type CommandOutcome, type SendLike } from './command-client.ts';

export type { SendLike } from './command-client.ts';

/**
 * Die Kennungsarten, die dieser Bildschirm anbietet -- die beschlossene
 * Startliste (ADR-0047 Ziffer 2, Michael Brozat 02.09.2026).
 *
 * DIE BINDUNG IST EINE PROBE UND KEIN VERSPRECHEN: apps/web/test/
 * identifiers.test.ts liest das juengste CHECK der Migrationen (0034, seit
 * 0075 erweitert) und prueft, dass
 * beide Listen dieselben Codes tragen. Waechst das Vokabular, faellt die
 * Probe -- nicht der Bildschirm.
 *
 * Der `issuerSuggestion` ist ein VORSCHLAG und kein Zwang: bei Verkehrsnummer
 * und GLN ist die ausstellende Stelle praktisch immer dieselbe, bei einer
 * Kundennummer ist sie das jeweilige Fachsystem und bleibt leer. Ein
 * vorbelegtes, editierbares Feld erspart das Nachschlagen, ohne eine Angabe zu
 * erfinden -- geaendert werden darf es immer.
 *
 * `issuerDerived`: die ausstellende Stelle leitet der Server aus dem Wert ab;
 * das Formular fragt nicht danach. `organizationOnly`: nur eine Organisation
 * traegt diese Art, an einer Person bietet der Bildschirm sie nicht an (der
 * Server weist sie dort ab).
 */
export const OFFERED_IDENTIFIER_TYPES: readonly {
  readonly code: string;
  readonly label: string;
  readonly hint: string;
  readonly issuerSuggestion?: string;
  readonly issuerDerived?: true;
  readonly organizationOnly?: true;
}[] = Object.freeze([
  Object.freeze({
    code: 'verkehrsnummer',
    label: 'Verkehrsnummer',
    hint: 'Die Kennung einer Buchhandlung oder eines Verlags im deutschen Buchhandel.',
    issuerSuggestion: 'MVB',
  }),
  Object.freeze({
    code: 'gln',
    label: 'GLN',
    hint: 'Die Globale Lokationsnummer eines Unternehmens oder Standorts.',
    issuerSuggestion: 'GS1',
  }),
  Object.freeze({
    code: 'kundennummer',
    label: 'Kundennummer',
    hint: 'Die Nummer, unter der ein Fachsystem diesen Kontakt führt. Nenne das System als ausstellende Stelle.',
  }),
  // Signatur S2 (Migration 0075): die Form prueft der Server, die Saetze am
  // Feld kommen aus seiner Antwort. Bei der USt-IdNr. leitet er die
  // ausstellende Stelle aus der Laenderkennung ab.
  Object.freeze({
    code: 'commercial_register',
    label: 'Handelsregister',
    hint: 'Registerart und Nummer, z. B. HRB 123456. Nenne das Registergericht als ausstellende Stelle.',
    organizationOnly: true,
  }),
  Object.freeze({
    code: 'vat_id',
    label: 'USt-IdNr.',
    hint: 'Länderkennung und Nummer, z. B. DE123456789. Das Land gilt als ausstellende Stelle.',
    issuerDerived: true,
    organizationOnly: true,
  }),
]);

/**
 * Die Arten, die an diesem Kontakt angeboten werden. Ist die Art des Kontakts
 * unbekannt (aelteres Image), bleibt das ganze Angebot stehen und der Server
 * antwortet mit seinem Feldfehler.
 */
export function offeredIdentifierTypesFor(kind: PartyKind | undefined): typeof OFFERED_IDENTIFIER_TYPES {
  return kind === 'person' ? OFFERED_IDENTIFIER_TYPES.filter((eintrag) => eintrag.organizationOnly !== true) : OFFERED_IDENTIFIER_TYPES;
}

/** Fachbegriffe der Kennungsarten -- fuer Listen und Historie. */
export const IDENTIFIER_TYPE_LABELS: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(OFFERED_IDENTIFIER_TYPES.map((eintrag) => [eintrag.code, eintrag.label])),
);

/**
 * Der Rueckfall nennt den Rohwert und behauptet nichts -- dieselbe Regel wie
 * bei jedem Vokabular dieses Bestands: waechst die Liste, erscheint der neue
 * Code sichtbar unuebersetzt, statt dass jemand ein Wort erfindet.
 */
export function identifierTypeLabel(code: string): string {
  return IDENTIFIER_TYPE_LABELS[code] ?? code;
}

// Die Praefixe, die der Server bei der USt-IdNr. annimmt (VAT_ID_NUMBER_PATTERNS
// in apps/api, VIES); identifiers.test.ts haelt die Liste dagegen. Der Wert
// ist die Form NACH "von": zwei Laender brauchen einen Artikel (Restliste
// B12b), EL und XI sind keine ISO-Codes und Intl kennt sie nicht.
const VAT_ISSUER_AFTER_VON: Readonly<Record<string, string | undefined>> = Object.freeze({
  AT: undefined, BE: undefined, BG: undefined, CY: undefined, CZ: undefined, DE: undefined, DK: undefined,
  EE: undefined, EL: 'Griechenland', ES: undefined, FI: undefined, FR: undefined, HR: undefined, HU: undefined,
  IE: undefined, IT: undefined, LT: undefined, LU: undefined, LV: undefined, MT: undefined, NL: 'den Niederlanden',
  PL: undefined, PT: undefined, RO: undefined, SE: undefined, SI: undefined, SK: 'der Slowakei', XI: 'Nordirland',
});

/** Die Praefixe der USt-IdNr., die der Server annimmt. */
export const VAT_ISSUER_PREFIXES: readonly string[] = Object.freeze(Object.keys(VAT_ISSUER_AFTER_VON));

/**
 * Die ausstellende Stelle in Worten, so wie sie nach "ausgestellt von" steht:
 * bei der USt-IdNr. das Land ("von Deutschland", "von den Niederlanden",
 * Restliste B12/B12b), weil der Server dort das Praefix als Aussteller
 * speichert. Gericht, MVB oder GS1 bleiben wie gespeichert. Ein Praefix
 * ausserhalb der Serverliste bleibt als Code stehen: sonst kaeme "von Schweiz"
 * oder, fuer "ZZ", "Unbekannte Region" heraus.
 */
export function issuerLabel(identifierType: string, issuer: string): string {
  if (identifierType !== 'vat_id' || !Object.hasOwn(VAT_ISSUER_AFTER_VON, issuer)) return issuer;
  return VAT_ISSUER_AFTER_VON[issuer] ?? countryName(issuer);
}

/**
 * Der Synchronisationsstand in Worten.
 *
 * Die Werte stammen aus der CHECK-Liste in db/migrations/0002. `observed`
 * heisst "erfasst": eine von Hand eingetragene Kennung ist eine Beobachtung
 * und kein Abgleich -- genau die Auskunft, die die Story mit "unbelegt statt
 * bestaetigt" verlangt.
 */
export const SYNCHRONIZATION_LABELS: Readonly<Record<string, string>> = Object.freeze({
  observed: 'nicht abgeglichen',
  synchronized: 'mit dem Quellsystem abgeglichen',
  conflict: 'widersprüchlich zum Quellsystem',
  retired: 'im Quellsystem zurückgezogen',
});

export function synchronizationLabel(code: string): string {
  return SYNCHRONIZATION_LABELS[code] ?? code;
}

/** Die Herkunft in Worten -- `liaison_ui` ist eine Handeingabe. */
export const SOURCE_SYSTEM_LABELS: Readonly<Record<string, string>> = Object.freeze({
  liaison_ui: 'von Hand erfasst',
});

export function sourceSystemLabel(code: string): string {
  return SOURCE_SYSTEM_LABELS[code] ?? code;
}

export interface IdentifierView {
  readonly identifierId: string;
  readonly identifierType: string;
  readonly issuer: string;
  readonly uniquenessScope: string;
  readonly value: string;
  readonly isPrimary: boolean;
  readonly verificationStatus: string;
  readonly synchronizationStatus: string;
  readonly sourceSystem: string;
  readonly validFrom: string;
  readonly lockVersion: number;
}

export interface EndedIdentifierView {
  readonly identifierId: string;
  readonly identifierType: string;
  readonly issuer: string;
  readonly value: string;
  readonly endedAt: string;
}

export interface PartyIdentifiers {
  /**
   * Der Anzeigename der Party (ST-SEA-001, Schnitt "Anzeige") -- aus
   * DERSELBEN Antwort wie die Kennungen, ohne zweiten Aufruf. Optional: ein
   * aelterer Server liefert ihn nicht, dann bleibt die Ueberschrift weg statt
   * eines erfundenen Namens.
   */
  readonly displayName?: string;
  readonly identifiers: readonly IdentifierView[];
  readonly endedIdentifiers: readonly EndedIdentifierView[];
  readonly additionalIdentifierCount: number;
  /** Der Lebenszyklus des Kontakts; fehlt er (aelteres Image), bleibt er unbekannt und das Angebot steht. */
  readonly lifecycleStatus?: string;
  /** Person oder Organisation; fehlt sie (aelteres Image), bleibt sie unbekannt. */
  readonly kind?: PartyKind;
  readonly endedIdentifiersTruncated: boolean;
}

export type PartyKind = 'person' | 'organization';

export type IdentifiersResult =
  | { readonly outcome: 'loaded'; readonly data: PartyIdentifiers }
  | { readonly outcome: 'not-found' }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

export type CommandResult = CommandOutcome<{ readonly identifierType: string; readonly lockVersion: number }>;

/**
 * Die Saetze der vier Konfliktlagen. Jede nennt Ursache und WEG ZURUECK
 * (NFR-UX-005); keiner verspricht ein "erneut versuchen", wo ein zweiter
 * Versuch mit demselben Stand genauso endete.
 */
export const CONFLICT_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Jemand anderes war schneller.',
  explanation: 'Diese Kennung hat sich geändert, seit du diese Seite geöffnet hast. Deine Eingabe wurde nicht gespeichert.',
  nextStep: 'Lade die Kennungen neu und entscheide dann noch einmal.',
  retryable: false,
});

export const ALREADY_ENDED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diese Kennung wird bereits nicht mehr verwendet.',
  explanation: 'Eine andere Person hat sie inzwischen auf „Nicht mehr verwenden“ gesetzt. Ein zweites Mal geht das nicht.',
  nextStep: 'Lade die Kennungen neu; sie steht dann unter „Frühere Kennungen“.',
  retryable: false,
  // Der gewollte Zustand gilt schon: ein Hinweis, kein Fehler.
  notice: true,
});

/**
 * Der Satz zur bereits vergebenen Kennung. Er sagt AUSDRUECKLICH NICHT, wo
 * sie vergeben ist: der Server nennt keinen Treffer, und die Oberflaeche darf
 * keinen erfinden (ADR-0047 Ziffer 5 -- die Antwort ist ein Bit, kein
 * Datensatz).
 */
export const VALUE_CONFLICT_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diese Kennung ist bereits vergeben.',
  explanation: 'Aussteller, Art und Wert sind in LIAISON schon eingetragen — bei diesem oder einem anderen Kontakt.',
  nextStep: 'Prüfe den Wert auf Tippfehler. Stimmt er, suche den Kontakt, der die Kennung trägt, in der Kontaktliste.',
  retryable: false,
});

/**
 * Der Satz zum Primaerkonflikt. Er nennt den Weg, den ADR-0047 Ziffer 3
 * verlangt: erst entziehen, dann erklaeren -- zwei sichtbare Entscheidungen.
 */
export const PRIMARY_CONFLICT_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Es gibt schon eine primäre Kennung dieser Art.',
  explanation: 'Je Aussteller und Art führt LIAISON höchstens eine primäre Kennung. LIAISON wechselt sie nicht von selbst.',
  nextStep: 'Entziehe der bisherigen Kennung zuerst den Primärstatus und lege diese danach als primär fest.',
  retryable: false,
});

/**
 * Der Satz zu einer Kennung an einem Kontakt, der nichts Neues mehr annimmt.
 * Nur das Anlegen liefert diesen Grund; derselbe Wortlaut wie beim Kontaktweg
 * (TM-017).
 */
export const IDENTIFIER_NOT_ADDABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'An diesem Kontakt lässt sich keine Kennung mehr eintragen.',
  explanation: 'Der Kontakt ist archiviert, anonymisiert oder mit einem anderen zusammengeführt.',
  nextStep: 'Geh zurück zum Kontakt und prüfe seinen Stand.',
  retryable: false,
});

export const NOT_FOUND_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diese Kennung gibt es hier nicht.',
  // KEINE VERMUTUNG UEBER EINE BERECHTIGUNG -- dieselbe Zusage wie ueberall:
  // der Server antwortet auf "gibt es nicht" und "darfst du nicht sehen" mit
  // demselben Ausgang.
  explanation: 'Sie wurde entfernt, oder die Adresse ist nicht mehr gültig.',
  nextStep: 'Geh zurück zum Kontakt und öffne die Kennungen dort erneut.',
  retryable: false,
});

/**
 * Das Absenden ist fehlgeschlagen -- dieselbe Lage und dieselbe Zusage wie bei
 * den Kontaktwegen: die Wiederholung traegt dieselbe Kennung des Absendens,
 * und der Server legt nichts doppelt an (ADR-0060).
 */
export const SUBMIT_FAILED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Das Absenden ist fehlgeschlagen.',
  explanation:
    'Die Verbindung ist abgebrochen, bevor LIAISON geantwortet hat. Ob die Kennung schon gespeichert ist, ist offen. Deine Eingabe steht noch im Formular.',
  nextStep: 'Wähle „Erneut versuchen“. Ist die Kennung schon angekommen, legt LIAISON sie kein zweites Mal an.',
  retryable: true,
});

/** Der erste Versuch kam mit anderen Angaben an; die jetzige Eingabe bleibt stehen. */
export const REQUEST_REUSED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Dein erster Versuch ist mit anderen Angaben angekommen.',
  explanation: 'Deine jetzige Eingabe wurde nicht gespeichert. Sie steht noch im Formular.',
  nextStep: 'Lade die Kennungen neu und prüfe, ob die Kennung schon da ist. Fehlt sie, füge sie danach hinzu.',
  retryable: false,
});

/**
 * Der erste Versuch ist angekommen, die Kennung wurde seither geaendert oder
 * beendet. Das Formular wird geleert wie nach einem Erfolg.
 */
export const REQUEST_SUPERSEDED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diese Kennung ist schon angelegt und wurde inzwischen geändert.',
  explanation:
    'Dein erster Versuch ist angekommen, das Formular ist deshalb wieder leer. Danach hat jemand die Kennung geändert oder auf „Nicht mehr verwenden“ gesetzt; ein weiterer Versuch ändert daran nichts.',
  nextStep: 'Lade die Kennungen neu und sieh nach, was jetzt gilt.',
  retryable: false,
});

function identifiersOf(value: unknown): readonly IdentifierView[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((entry: unknown) => {
    const row = (entry ?? {}) as Record<string, unknown>;
    if (typeof row.identifierId !== 'string' || typeof row.value !== 'string') {
      return [];
    }
    return [
      {
        identifierId: row.identifierId,
        identifierType: typeof row.identifierType === 'string' ? row.identifierType : '',
        issuer: typeof row.issuer === 'string' ? row.issuer : '',
        uniquenessScope: typeof row.uniquenessScope === 'string' ? row.uniquenessScope : '',
        value: row.value,
        isPrimary: row.isPrimary === true,
        verificationStatus: typeof row.verificationStatus === 'string' ? row.verificationStatus : '',
        synchronizationStatus: typeof row.synchronizationStatus === 'string' ? row.synchronizationStatus : '',
        sourceSystem: typeof row.sourceSystem === 'string' ? row.sourceSystem : '',
        validFrom: typeof row.validFrom === 'string' ? row.validFrom : '',
        // 0 heisst "unbekannt": ein Sperrstand, den der Server nie fuehrt.
        // Ein Befehl damit endet als 409 statt als stilles Ueberschreiben.
        lockVersion: typeof row.lockVersion === 'number' ? row.lockVersion : 0,
      },
    ];
  });
}

function endedIdentifiersOf(value: unknown): readonly EndedIdentifierView[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((entry: unknown) => {
    const row = (entry ?? {}) as Record<string, unknown>;
    if (typeof row.identifierId !== 'string' || typeof row.value !== 'string') {
      return [];
    }
    return [
      {
        identifierId: row.identifierId,
        identifierType: typeof row.identifierType === 'string' ? row.identifierType : '',
        issuer: typeof row.issuer === 'string' ? row.issuer : '',
        value: row.value,
        endedAt: typeof row.endedAt === 'string' ? row.endedAt : '',
      },
    ];
  });
}

export interface ReadOptions {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly send?: SendLike;
}

export async function fetchIdentifiers(options: ReadOptions): Promise<IdentifiersResult> {
  const result = await readResource({
    url: `${options.baseUrl}/parties/${encodeURIComponent(options.partyId)}/identifiers`,
    accessToken: options.accessToken,
    ...(options.send === undefined ? {} : { send: options.send }),
  });
  if (result.outcome !== 'loaded') {
    return result;
  }
  const body = result.body;
  return {
    outcome: 'loaded',
    data: {
      ...(typeof body.displayName === 'string' && body.displayName.length > 0 ? { displayName: body.displayName } : {}),
      identifiers: identifiersOf(body.identifiers),
      endedIdentifiers: endedIdentifiersOf(body.endedIdentifiers),
      additionalIdentifierCount:
        typeof body.additionalIdentifierCount === 'number' ? body.additionalIdentifierCount : 0,
      ...(typeof body.lifecycleStatus === 'string' && body.lifecycleStatus.length > 0 ? { lifecycleStatus: body.lifecycleStatus } : {}),
      ...(body.kind === 'person' || body.kind === 'organization' ? { kind: body.kind } : {}),
      endedIdentifiersTruncated: body.endedIdentifiersTruncated === true,
    },
  };
}

/** Wie der Befehls-Client die Antworten der Kennungspflege in Saetze uebersetzt. */
const COMMAND_MESSAGES: CommandMessages = Object.freeze({
  notFound: NOT_FOUND_MESSAGE,
  conflictFallback: CONFLICT_MESSAGE,
  conflicts: Object.freeze({
    party_identifier_already_ended: ALREADY_ENDED_MESSAGE,
    party_identifier_value_conflict: VALUE_CONFLICT_MESSAGE,
    party_identifier_primary_conflict: PRIMARY_CONFLICT_MESSAGE,
    party_not_writable: IDENTIFIER_NOT_ADDABLE_MESSAGE,
    client_request_reused: REQUEST_REUSED_MESSAGE,
    client_request_superseded: REQUEST_SUPERSEDED_MESSAGE,
  }),
});

function sendIdentifierCommand(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly path: string;
  readonly body: Record<string, unknown>;
  readonly send?: SendLike;
}): Promise<CommandResult> {
  return sendCommand({
    url: `${options.baseUrl}${options.path}`,
    accessToken: options.accessToken,
    body: options.body,
    messages: COMMAND_MESSAGES,
    applied: (record) => ({
      identifierType: typeof record.identifierType === 'string' ? record.identifierType : '',
      lockVersion: typeof record.lockVersion === 'number' ? record.lockVersion : 0,
    }),
    ...(options.send === undefined ? {} : { send: options.send }),
  });
}

export interface CommandOptions {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly send?: SendLike;
}

/** `clientRequestId` ist Pflicht -- dieselbe Begruendung wie bei `addContactPoint`. */
export function addIdentifier(
  options: CommandOptions & {
    readonly identifierType: string;
    readonly issuer: string;
    readonly value: string;
    readonly clientRequestId: string;
  },
): Promise<CommandResult> {
  return sendIdentifierCommand({
    baseUrl: options.baseUrl,
    accessToken: options.accessToken,
    path: `/parties/${encodeURIComponent(options.partyId)}/identifiers`,
    body: {
      identifierType: options.identifierType,
      issuer: options.issuer,
      value: options.value,
      clientRequestId: options.clientRequestId,
    },
    ...(options.send === undefined ? {} : { send: options.send }),
  });
}

export function endIdentifier(
  options: CommandOptions & { readonly identifierId: string; readonly lockVersion: number },
): Promise<CommandResult> {
  return sendIdentifierCommand({
    baseUrl: options.baseUrl,
    accessToken: options.accessToken,
    path: `/parties/${encodeURIComponent(options.partyId)}/identifiers/${encodeURIComponent(options.identifierId)}/end`,
    body: { lockVersion: options.lockVersion },
    ...(options.send === undefined ? {} : { send: options.send }),
  });
}

export function setIdentifierPrimary(
  options: CommandOptions & { readonly identifierId: string; readonly isPrimary: boolean; readonly lockVersion: number },
): Promise<CommandResult> {
  return sendIdentifierCommand({
    baseUrl: options.baseUrl,
    accessToken: options.accessToken,
    path: `/parties/${encodeURIComponent(options.partyId)}/identifiers/${encodeURIComponent(options.identifierId)}/primary`,
    body: { isPrimary: options.isPrimary, lockVersion: options.lockVersion },
    ...(options.send === undefined ? {} : { send: options.send }),
  });
}

/**
 * Die Bestaetigung nennt die WIRKUNG und nicht den Vorgang -- dieselbe Regel
 * wie bei den Kontaktwegen (CM-UX-004: Wirkung UND Rueckweg).
 */
export function confirmationFor(
  operation: 'add' | 'end' | 'primary-set' | 'primary-unset',
  identifierType: string,
): string {
  const art = identifierTypeLabel(identifierType);
  if (operation === 'add') {
    return `${art} hinzugefügt. Die Kennung gilt ab sofort und ist nicht primär.`;
  }
  if (operation === 'end') {
    return `${art} wird nicht mehr verwendet. Die Kennung steht jetzt unter „Frühere Kennungen“ und bleibt dort nachlesbar.`;
  }
  if (operation === 'primary-set') {
    return `${art} ist jetzt die primäre Kennung ihrer Art.`;
  }
  return `${art} ist nicht mehr primär. Es gibt jetzt keine primäre Kennung dieser Art, bis du eine festlegst.`;
}

/**
 * Der Hinweis auf gueltige Kennungen, die nicht in die Antwort gepasst haben.
 * Er nennt DIE ZAHL -- eine Obergrenze ohne Zahl waere ein stilles Weglassen
 * (Praezedenz additionalContactPointCount).
 */
export function additionalIdentifierNotice(count: number): string | undefined {
  if (count <= 0) {
    return undefined;
  }
  return count === 1
    ? 'Eine weitere Kennung passt nicht mehr in diese Ansicht. Setze nicht mehr benötigte auf „Nicht mehr verwenden“, dann erscheint sie.'
    : `${count} weitere Kennungen passen nicht mehr in diese Ansicht. Setze nicht mehr benötigte auf „Nicht mehr verwenden“, dann erscheinen sie.`;
}

/** Der Hinweis unter einer abgeschnittenen Historie -- ohne Zahl, der Server liefert keine. */
export const HISTORY_TRUNCATED_NOTICE = 'Es gibt weitere frühere Kennungen, die diese Ansicht nicht zeigt.';

/**
 * Der Satz zu einer abgewiesenen Eingabe, je Feld und Grund.
 *
 * Er sagt, WAS zu aendern ist, und nicht nur "ungueltig" (NFR-UX-005). Der
 * Rueckfall ist bewusst allgemein: ein Grundcode, den diese Fassung nicht
 * kennt, bekommt einen ehrlichen Satz statt eines erfundenen.
 */
export function validationMessageFor(fieldPath: string, reasonCode: string, identifierType?: string): string {
  if (fieldPath === 'value') {
    if (reasonCode === 'field_too_long') {
      return 'Der Wert ist zu lang für eine Kennung. Prüfe, ob versehentlich mehr als die Kennung eingefügt wurde.';
    }
    // Handelsregister und USt-IdNr. haben eine feste Form; der Satz nennt sie,
    // statt nur von Leerzeichen zu sprechen, die dort gar erlaubt sind.
    if (reasonCode === 'field_malformed' && identifierType === 'vat_id') {
      return 'Diese USt-IdNr. hat nicht die Form ihres Landes. Prüfe Länderkennung und Ziffern, z. B. DE und neun Ziffern.';
    }
    if (reasonCode === 'field_malformed' && identifierType === 'commercial_register') {
      return 'Diese Nummer hat nicht die Form eines Handelsregistereintrags. Trage Registerart und Nummer ein, z. B. HRB 123456.';
    }
    if (reasonCode === 'field_malformed') {
      return 'Der Wert darf keine Leerzeichen enthalten. Trage die Kennung ohne Trennzeichen ein.';
    }
    return 'Trage den Wert der Kennung ein.';
  }
  if (fieldPath === 'issuer') {
    if (reasonCode === 'field_too_long') {
      return 'Der Name der ausstellenden Stelle ist zu lang.';
    }
    return 'Nenne die Stelle, die diese Kennung vergeben hat.';
  }
  if (fieldPath === 'identifierType') {
    if (reasonCode === 'field_not_allowed') {
      return 'Handelsregister und USt-IdNr. gehören zu einer Organisation, nicht zu einer Person. Trage sie bei der Organisation ein.';
    }
    return 'Wähle eine der angebotenen Arten.';
  }
  return 'Prüfe die markierte Angabe.';
}
